import useStores from '@stores/useStores';
import { FeatureFlagKeys } from '@lib/experiment';
import { useFeatureFlagObserver } from '@hooks/useFeatureFlagObserver';
import {
  checkPermission,
  checkLockedResource,
  constructPermissionResult,
  constructReasonForNoPermission,
} from './utils';
import { ActionType, Query, QueryResult } from './types';
import { DEFAULT_PERMISSION_DENIED_REASON } from './constants';

export { ResourceType, ActionType } from './types';

export const useActionGuard = () => {
  const { permissionsStore, featuresStore, workspaceStore, userStore, organizationStore } =
    useStores();
  const { accessPolicy } = workspaceStore;

  const isNewPermissionModelEnforcementEnabled = useFeatureFlagObserver(
    FeatureFlagKeys.enableNewPermissionsModelEnforcement,
  );

  /**
   * Checks if the user can perform the given action based on the current permission model.
   *
   * - Always constructs a permission result with an `isAllowed` flag and a denial reason.
   * - Under the **new permission model**, a detailed and contextual denial reason is generated.
   * - Under the **old permission model**, a default denial reason is used.
   *   However, the default message can be overridden at the call site if needed.
   */
  const canPerformAction = (query: Query): QueryResult => {
    if (query.new) {
      const { isLocked, reason } = checkLockedResource(
        organizationStore.lockedResources,
        userStore.selectedWorkspaceId,
        query.new,
      );
      if (isLocked) {
        return { isAllowed: false, denialReason: reason };
      }
    }

    if (isNewPermissionModelEnforcementEnabled) {
      if (userStore.isOrgAdmin) {
        return { isAllowed: true };
      }

      if (
        !featuresStore.has('PII_PERMISSIONS') &&
        (query.new.actionType === ActionType.PII_LIVE_EVENTS ||
          query.new.actionType === ActionType.PII_FAILURE_SAMPLES)
      ) {
        return { isAllowed: true };
      }

      return constructPermissionResult(
        checkPermission(accessPolicy, query.new),
        constructReasonForNoPermission(query.new),
      );
    }

    return constructPermissionResult(
      !query.old || permissionsStore.hasPermission(query.old),
      DEFAULT_PERMISSION_DENIED_REASON,
    );
  };

  return { canPerformAction, isNewPermissionModelEnforcementEnabled };
};
